import { useUiContent, UI_CONTENT_KEYS } from '../lib/uiContent';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, ArrowUpRight, BrainCircuit, Cpu, Loader2, Radar, RefreshCw, ShieldCheck, Sparkles } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from 'recharts';
import { toast } from 'sonner';

import AICopilotWorkbench from '../components/ai/AICopilotWorkbench';
import AIActivityTimeline from '../components/ai/AIActivityTimeline';
import AIAnomalyTable from '../components/ai/AIAnomalyTable';
import AIInsightCard from '../components/ai/AIInsightCard';
import AIPredictiveTrendWidget from '../components/ai/AIPredictiveTrendWidget';
import AIRecommendationPanel from '../components/ai/AIRecommendationPanel';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { getErrorMessage } from '../lib/utils';
import { aiCommandAPI } from '../services/api';
import { chartColor, tooltipContentStyle, tooltipCursor, axisProps, gridProps } from '../lib/chartTheme';
import { EmptyState, StatSkeletonGrid } from '../components/common/States';

const STRIP_TONE = {
  primary: 'text-cyan-300 shadow-[0_0_0_1px_rgba(34,211,238,0.08)]',
  info: 'text-sky-300 shadow-[0_0_0_1px_rgba(56,189,248,0.08)]',
  success: 'text-emerald-300 shadow-[0_0_0_1px_rgba(16,185,129,0.08)]',
  warning: 'text-amber-300 shadow-[0_0_0_1px_rgba(245,158,11,0.08)]',
  danger: 'text-rose-300 shadow-[0_0_0_1px_rgba(244,63,94,0.08)]',
};

const LIGHT_PANEL = 'rounded-3xl border border-border bg-card text-foreground shadow-[0_18px_40px_rgba(15,23,42,0.06)]';
const LIGHT_SUBPANEL = 'rounded-2xl border border-border bg-muted';
const LIGHT_LABEL = 'text-[11px] uppercase tracking-[0.18em] text-muted-foreground';

const formatINR = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));


const buildDetailTab = (section, itemId) => `ai-command/detail/${encodeURIComponent(section)}/${encodeURIComponent(String(itemId))}`;
const slugify = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const buildInteractiveProps = (onClick) => ({
  role: 'button',
  tabIndex: 0,
  onClick,
  onKeyDown: (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  },
});

const DetailActionButton = ({ onClick, label = 'Open details' }) => (
  <button
    type="button"
    className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground transition hover:border-primary/40 hover:bg-primary/10 hover:text-primary"
    onClick={onClick}
  >
    {label}
    <ArrowUpRight className="h-3.5 w-3.5" />
  </button>
);

const AI_DASHBOARD_SECTIONS = [
  {
    key: 'overview',
    id: 'ai-command/overview',
    label: 'Overview',
    description: 'Executive summary strip and AI operating snapshot',
    icon: BrainCircuit,
  },
  {
    key: 'copilot',
    id: 'ai-command/copilot',
    label: 'Copilot',
    description: 'Prompt workbench and executive prompt lanes',
    icon: Sparkles,
  },
  {
    key: 'insights',
    id: 'ai-command/insights',
    label: 'Insights',
    description: 'Cross-tenant AI insights and synthesized actions',
    icon: Radar,
  },
  {
    key: 'monitoring',
    id: 'ai-command/monitoring',
    label: 'Monitoring',
    description: 'Tenant health matrix, heatmap, and risk distribution',
    icon: Activity,
  },
  {
    key: 'alerts',
    id: 'ai-command/alerts',
    label: 'Alerts',
    description: 'Anomalies, compliance drift, and alert pressure',
    icon: ShieldCheck,
  },
  {
    key: 'reports',
    id: 'ai-command/reports',
    label: 'Reports',
    description: 'Executive reports and predictive forecasting',
    icon: ArrowUpRight,
  },
  {
    key: 'governance',
    id: 'ai-command/governance',
    label: 'Governance',
    description: 'Model health, recommendations, and activity logs',
    icon: ShieldCheck,
  },
  {
    key: 'services',
    id: 'ai-command/services',
    label: 'Services',
    description: 'AI service mesh and operating status',
    icon: Cpu,
  },
];

const SuperadminAICommandCenterPage = ({ activeTab = 'ai-command/overview', onNavigate }) => {
  // Editable on Master → Platform Definitions rather than in this file.
  const uiContent = useUiContent(UI_CONTENT_KEYS.AI_PROMPT_LANES);
  const promptLanes = uiContent[UI_CONTENT_KEYS.AI_PROMPT_LANES] || [];
  const [loading, setLoading] = useState(true);
  const [seedLoading, setSeedLoading] = useState(false);
  const [data, setData] = useState({
    summary_strip: [],
    global_ai_insights: [],
    tenant_health_matrix: [],
    workforce_risk_heatmap: [],
    ai_anomaly_tracker: [],
    predictive_analytics: {},
    ai_governance_center: {},
    ai_alerts_center: [],
    ai_executive_reports: [],
  });

  const copilotConfig = useMemo(
    () => ({
      title: 'Ask AI For Platform Analytics',
      description: 'Query cross-tenant workforce intelligence, compliance posture, payroll exposure, and executive risk analytics from one operating layer.',
      suggestions: [
        'Which tenants have the highest attrition risk?',
        'Summarize payroll and finance exposure across tenants.',
        'Compare tenant compliance health and governance coverage.',
        'Benchmark tenants by hiring efficiency and AI risk.',
      ],
    }),
    []
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await aiCommandAPI.getSuperadminCommandCenter();
      setData(response.data || {});
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load AI Dashboard'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSeed = async () => {
    try {
      setSeedLoading(true);
      await aiCommandAPI.bootstrap();
      await loadData();
      toast.success('AI Dashboard data refreshed');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to refresh AI Dashboard data'));
    } finally {
      setSeedLoading(false);
    }
  };

  const handleSuperadminQuery = async (query) => {
    const response = await aiCommandAPI.querySuperadmin(query);
    return response.data || {};
  };

  const goToDetail = useCallback(
    (section, itemId) => {
      if (typeof onNavigate === 'function') {
        onNavigate(buildDetailTab(section, itemId));
      }
    },
    [onNavigate]
  );

  const planMix = useMemo(
    () => (data.tenant_health_matrix || []).map((row) => ({ name: row.tenant_name, value: row.ai_risk_score })),
    [data.tenant_health_matrix]
  );

  const currentSectionKey = useMemo(() => {
    const rawSection = String(activeTab || '')
      .replace(/^ai-command\/?/, '')
      .trim()
      .toLowerCase();
    if (!rawSection || rawSection === 'ai-command') {
      return 'overview';
    }
    return AI_DASHBOARD_SECTIONS.some((section) => section.key === rawSection) ? rawSection : 'overview';
  }, [activeTab]);

  const currentSection = useMemo(
    () => AI_DASHBOARD_SECTIONS.find((section) => section.key === currentSectionKey) || AI_DASHBOARD_SECTIONS[0],
    [currentSectionKey]
  );

  const navigationCards = useMemo(
    () => [
      {
        ...AI_DASHBOARD_SECTIONS.find((section) => section.key === 'overview'),
        metric: `${(data.summary_strip || []).length} executive metrics`,
      },
      {
        ...AI_DASHBOARD_SECTIONS.find((section) => section.key === 'copilot'),
        metric: `${promptLanes.length} prompt lanes`,
      },
      {
        ...AI_DASHBOARD_SECTIONS.find((section) => section.key === 'insights'),
        metric: `${(data.global_ai_insights || []).length} insights`,
      },
      {
        ...AI_DASHBOARD_SECTIONS.find((section) => section.key === 'monitoring'),
        metric: `${(data.tenant_health_matrix || []).length} tenants monitored`,
      },
      {
        ...AI_DASHBOARD_SECTIONS.find((section) => section.key === 'alerts'),
        metric: `${(data.ai_anomaly_tracker || []).length} active anomalies`,
      },
      {
        ...AI_DASHBOARD_SECTIONS.find((section) => section.key === 'reports'),
        metric: `${(data.ai_executive_reports || []).length} reports`,
      },
      {
        ...AI_DASHBOARD_SECTIONS.find((section) => section.key === 'governance'),
        metric: `${(data.ai_governance_center?.model_health || []).length} models tracked`,
      },
      {
        ...AI_DASHBOARD_SECTIONS.find((section) => section.key === 'services'),
        metric: `${(data.ai_governance_center?.microservices || []).length} services`,
      },
    ],
    [data, promptLanes.length]
  );

  const renderOverviewSection = () => (
    <section className="group relative overflow-hidden rounded-[28px] border border-border bg-gradient-to-br from-primary/10 via-card to-info/10 p-6 text-foreground shadow-[0_22px_60px_rgba(15,23,42,0.08)]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-16 top-2 h-44 w-44 rounded-full bg-primary/10 blur-3xl transition duration-700 group-hover:scale-110" />
        <div className="absolute right-0 top-6 h-48 w-48 rounded-full bg-info/10 blur-3xl transition duration-700 group-hover:scale-110" />
      </div>
      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card/80 px-3 py-1 text-xs font-medium text-primary shadow-sm backdrop-blur-sm">
            <BrainCircuit className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-12" />
            AI Dashboard
          </div>
          <div>
            <h1 className="text-hr-h1 font-bold tracking-tight text-foreground">AI Dashboard</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground [text-wrap:balance]">
              Platform-wide workforce intelligence, AI governance, anomaly monitoring, compliance visibility, and executive decision support across tenants.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {(data.summary_strip || []).map((item) => (
              <div
                key={item.key}
                className="group min-h-[148px] cursor-pointer overflow-hidden rounded-2xl border border-border bg-card/90 p-4 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
                {...buildInteractiveProps(() => goToDetail('summary', item.key))}
              >
                <div className="flex h-full flex-col justify-between gap-3">
                  <div>
                    <p className="max-w-[12rem] text-[11px] uppercase tracking-[0.22em] text-muted-foreground break-words [text-wrap:balance]">{item.label}</p>
                  </div>
                  <div>
                    <p className="max-w-full break-words text-[18px] font-semibold leading-none text-foreground transition-transform duration-300 group-hover:translate-x-0.5">
                      {item.format === 'currency' ? formatINR(item.value) : item.value}
                    </p>
                    <p className="mt-3 max-w-[14rem] text-xs leading-5 text-muted-foreground break-words [text-wrap:balance]">{item.delta}</p>
                    <div className="mt-4">
                      <span
                        className={`inline-flex items-center rounded-full border border-current/15 bg-current/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.18em] ${STRIP_TONE[item.tone] || STRIP_TONE.primary}`}
                      >
                        {item.tone || 'primary'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
          <div
            className="group min-h-[252px] cursor-pointer rounded-2xl border border-border bg-card/90 p-5 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
            {...buildInteractiveProps(() => goToDetail('governance-hero', 'overview'))}
          >
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">AI Governance</p>
              <div className="rounded-full border border-emerald-500/30 bg-emerald-500/10 p-1.5">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
              </div>
            </div>
            <p className="mt-5 max-w-[26rem] text-[1.75rem] font-semibold leading-[1.35] tracking-tight text-foreground [text-wrap:balance]">
              Decision confidence and policy enforcement stay visible across the platform.
            </p>
            <p className="mt-4 max-w-[26rem] text-sm leading-7 text-muted-foreground [text-wrap:pretty]">
              Prompt logging, approvals, explainability, and control coverage run from a centralized operating layer.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 xl:justify-end">
            <Button variant="outline" className="min-w-[132px] border-border bg-card/90 text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary/30 hover:bg-primary/10 hover:text-primary" onClick={loadData}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button className="min-w-[168px] bg-primary text-primary-foreground shadow-sm transition hover:-translate-y-0.5 hover:bg-primary/90" onClick={handleSeed} disabled={seedLoading}>
              {seedLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Refresh AI Data
            </Button>
          </div>
        </div>
      </div>
    </section>
  );

  const renderCopilotSection = () => (
    <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
      <div className="space-y-3">
        <AICopilotWorkbench config={copilotConfig} onSubmit={handleSuperadminQuery} variant="light" />
      </div>

      <Card className={LIGHT_PANEL}>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Radar className="h-4 w-4 text-primary" />
            Executive Prompt Lanes
          </CardTitle>
          <DetailActionButton onClick={() => goToDetail('prompt', 'tenant-risk-watch')} />
        </CardHeader>
        <div className="px-6 text-sm text-muted-foreground">Fast-launch intelligence paths for risk, governance, and commercial decisions.</div>
        <CardContent className="space-y-3">
          {promptLanes.map((item) => (
            <div
              key={item.title}
              className={`rounded-2xl border border-border bg-gradient-to-r ${item.accent} p-4 shadow-sm cursor-pointer transition hover:-translate-y-0.5 hover:border-primary/30 focus:outline-none focus:ring-2 focus:ring-primary/40`}
              {...buildInteractiveProps(() => goToDetail('prompt', slugify(item.title)))}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">{item.title}</p>
                  <p className="mt-2 text-sm text-muted-foreground">{item.summary}</p>
                </div>
                <div className="rounded-xl border border-border bg-card p-2 text-primary shadow-sm">
                  <ArrowUpRight className="h-4 w-4" />
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );

  const renderInsightsSection = () => (
    <Card className={LIGHT_PANEL}>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-foreground">Global AI Insights</CardTitle>
          <CardDescription className="text-muted-foreground">Cross-tenant signals synthesized into executive actions.</CardDescription>
        </div>
        <DetailActionButton onClick={() => goToDetail('section', 'global-ai-insights')} />
      </CardHeader>
      <CardContent className="grid gap-4 xl:grid-cols-2">
        {(data.global_ai_insights || []).map((insight) => (
          <div
            key={insight.id}
            className="cursor-pointer rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/40"
            {...buildInteractiveProps(() => goToDetail('insight', insight.id))}
          >
            <AIInsightCard insight={insight} variant="light" />
          </div>
        ))}
      </CardContent>
    </Card>
  );

  const renderAttendanceInsightSection = () => {
    const attendanceRows = (data.workforce_risk_heatmap || []).slice(0, 8);
    const stabilityRows = [...(data.tenant_health_matrix || [])]
      .sort((left, right) => Number(right.workforce_stability || 0) - Number(left.workforce_stability || 0))
      .slice(0, 5);

    return (
      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="space-y-6">
          <div className="cursor-pointer rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/40" {...buildInteractiveProps(() => goToDetail('predictive', 'workforce-forecasting'))}>
            <AIPredictiveTrendWidget
              title="Attendance Continuity"
              description="Modeled workforce continuity and attendance-linked stability"
              data={data.predictive_analytics?.workforce_forecasting || []}
              color="#22d3ee"
              variant="light"
            />
          </div>

          <Card className={LIGHT_PANEL}>
            <CardHeader>
              <CardTitle className="text-foreground">Attendance Hotspots</CardTitle>
              <CardDescription className="text-muted-foreground">Tenants showing the sharpest attendance and workforce-risk pulse.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2">
              {attendanceRows.map((row) => (
                <div
                  key={row.tenant_name}
                  className="cursor-pointer rounded-2xl border border-border bg-muted p-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  {...buildInteractiveProps(() => goToDetail('heatmap', slugify(row.tenant_name)))}
                >
                  <p className="text-sm font-semibold text-foreground">{row.tenant_name}</p>
                  <p className="mt-2 text-xs text-muted-foreground">Risk {row.risk} and stability {row.stability}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <Card className={LIGHT_PANEL}>
          <CardHeader>
            <CardTitle className="text-foreground">Workforce Stability Leaders</CardTitle>
            <CardDescription className="text-muted-foreground">Tenant attendance posture paired with workforce stability and attrition pressure.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {stabilityRows.map((row) => (
              <div
                key={row.tenant_id}
                className="cursor-pointer rounded-2xl border border-border bg-card p-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                {...buildInteractiveProps(() => goToDetail('tenant', row.tenant_id))}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{row.tenant_name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{row.region} • {row.department_focus}</p>
                  </div>
                  <div className="text-right">
                    <p className={LIGHT_LABEL}>Stability</p>
                    <p className="mt-1 text-lg font-semibold text-foreground">{row.workforce_stability}</p>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>Attrition</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{row.attrition_risk}%</p>
                  </div>
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>AI Risk</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{row.ai_risk_score}</p>
                  </div>
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>Alerts</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{row.alert_count}</p>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderHiringForecastSection = () => {
    const hiringRows = [...(data.tenant_health_matrix || [])]
      .sort((left, right) => Number(right.hiring_efficiency || 0) - Number(left.hiring_efficiency || 0))
      .slice(0, 6);

    return (
      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="space-y-6">
          <div className="cursor-pointer rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/40" {...buildInteractiveProps(() => goToDetail('predictive', 'hiring-demand-prediction'))}>
            <AIPredictiveTrendWidget
              title="Hiring Forecast"
              description="Projected hiring demand across the managed tenant base"
              data={data.predictive_analytics?.hiring_demand_prediction || []}
              color="#818cf8"
              variant="light"
            />
          </div>

          <Card className={LIGHT_PANEL}>
            <CardHeader>
              <CardTitle className="text-foreground">Hiring Efficiency Benchmark</CardTitle>
              <CardDescription className="text-muted-foreground">Tenants ranked by pipeline efficiency and hiring readiness.</CardDescription>
            </CardHeader>
            <CardContent className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hiringRows}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="tenant_name" {...axisProps} />
                  <YAxis {...axisProps} />
                  <Tooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                  <Bar dataKey="hiring_efficiency" radius={[6, 6, 0, 0]} fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <Card className={LIGHT_PANEL}>
          <CardHeader>
            <CardTitle className="text-foreground">Hiring Forecast Targets</CardTitle>
            <CardDescription className="text-muted-foreground">Pipeline-ready tenants with commercial and workforce context.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {hiringRows.map((row) => (
              <div
                key={row.tenant_id}
                className="cursor-pointer rounded-2xl border border-border bg-card p-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                {...buildInteractiveProps(() => goToDetail('tenant', row.tenant_id))}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{row.tenant_name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{row.region} • {row.status}</p>
                  </div>
                  <div className="text-right">
                    <p className={LIGHT_LABEL}>Hiring</p>
                    <p className="mt-1 text-lg font-semibold text-foreground">{row.hiring_efficiency}</p>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>Payroll</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{formatINR(row.payroll_volume)}</p>
                  </div>
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>AI Risk</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{row.ai_risk_score}</p>
                  </div>
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>Alerts</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{row.alert_count}</p>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderPayrollForecastSection = () => {
    const payrollAlerts = (data.ai_anomaly_tracker || []).slice(0, 6);

    return (
      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="space-y-6">
          <div className="cursor-pointer rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/40" {...buildInteractiveProps(() => goToDetail('predictive', 'payroll-forecasting'))}>
            <AIPredictiveTrendWidget
              title="Payroll Forecast"
              description="Projected payroll exposure across active subscriptions"
              data={data.predictive_analytics?.payroll_forecasting || []}
              color="#10b981"
              variant="light"
              valueFormatter={formatINR}
            />
          </div>

          <Card className={LIGHT_PANEL}>
            <CardHeader>
              <CardTitle className="text-foreground">Payroll Exposure Snapshot</CardTitle>
              <CardDescription className="text-muted-foreground">The current tenant mix driving payroll and finance pressure.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2">
              {(data.tenant_health_matrix || []).slice(0, 4).map((row) => (
                <div
                  key={row.tenant_id}
                  className="cursor-pointer rounded-2xl border border-border bg-muted p-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  {...buildInteractiveProps(() => goToDetail('tenant', row.tenant_id))}
                >
                  <p className="text-sm font-semibold text-foreground">{row.tenant_name}</p>
                  <p className="mt-2 text-xs text-muted-foreground">{row.region} • {row.status}</p>
                  <p className="mt-3 text-lg font-semibold text-foreground">{formatINR(row.payroll_volume)}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <Card className={LIGHT_PANEL}>
          <CardHeader>
            <CardTitle className="text-foreground">Payroll Exposure Scan</CardTitle>
            <CardDescription className="text-muted-foreground">Anomalies and financial movement signals surfaced by the AI layer.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {payrollAlerts.map((row) => (
              <div
                key={row.id}
                className="cursor-pointer rounded-2xl border border-border bg-card p-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                {...buildInteractiveProps(() => goToDetail('alert', row.id))}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{row.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{row.tenant_name || row.description}</p>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${String(row.severity).toLowerCase() === 'high' ? 'bg-rose-500/10 text-rose-600' : 'bg-amber-500/10 text-amber-600'}`}>
                    {row.severity}
                  </span>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>Risk</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{Number(row.risk_score || 0).toFixed(1)}</p>
                  </div>
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>Status</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{row.status}</p>
                  </div>
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>Tenant</p>
                    <p className="mt-2 text-sm font-semibold text-foreground">{row.tenant_name || 'Platform'}</p>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderTeamPerformanceSection = () => {
    const performanceRows = [...(data.tenant_health_matrix || [])]
      .sort((left, right) => Number(right.workforce_stability || 0) - Number(left.workforce_stability || 0))
      .slice(0, 6);

    return (
      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="space-y-6">
          <div className="cursor-pointer rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/40" {...buildInteractiveProps(() => goToDetail('predictive', 'productivity-forecasting'))}>
            <AIPredictiveTrendWidget
              title="Team Performance"
              description="Projected productivity and team-stability trend across tenants"
              data={data.predictive_analytics?.productivity_forecasting || []}
              color="#22d3ee"
              variant="light"
            />
          </div>

          <Card className={LIGHT_PANEL}>
            <CardHeader>
              <CardTitle className="text-foreground">Performance Distribution</CardTitle>
              <CardDescription className="text-muted-foreground">Workforce stability compared with attrition pressure and alerts.</CardDescription>
            </CardHeader>
            <CardContent className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={performanceRows}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="tenant_name" {...axisProps} />
                  <YAxis {...axisProps} />
                  <Tooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                  <Bar dataKey="workforce_stability" radius={[6, 6, 0, 0]} fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <Card className={LIGHT_PANEL}>
          <CardHeader>
            <CardTitle className="text-foreground">Tenant Performance Signals</CardTitle>
            <CardDescription className="text-muted-foreground">Performance-leading tenants with the clearest stability and engagement position.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {performanceRows.map((row) => (
              <div
                key={row.tenant_id}
                className="cursor-pointer rounded-2xl border border-border bg-card p-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                {...buildInteractiveProps(() => goToDetail('tenant', row.tenant_id))}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{row.tenant_name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{row.region} • {row.department_focus}</p>
                  </div>
                  <div className="text-right">
                    <p className={LIGHT_LABEL}>Stability</p>
                    <p className="mt-1 text-lg font-semibold text-foreground">{row.workforce_stability}</p>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>Hiring</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{row.hiring_efficiency}</p>
                  </div>
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>Attrition</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{row.attrition_risk}%</p>
                  </div>
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>Alerts</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{row.alert_count}</p>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderMonitoringSection = () => (
    <div className="grid gap-6">
      <Card className={LIGHT_PANEL}>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-foreground">Multi-Tenant Monitoring</CardTitle>
            <CardDescription className="text-muted-foreground">Health matrix, risk benchmarking, and tenant comparison.</CardDescription>
          </div>
          <DetailActionButton onClick={() => goToDetail('section', 'multi-tenant-monitoring')} />
        </CardHeader>
        <CardContent className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-3">
            {(data.tenant_health_matrix || []).map((row, index) => (
              <div
                key={row.tenant_id}
                className="animate-slide-up cursor-pointer rounded-2xl border border-border bg-card p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
                style={{ animationDelay: `${index * 0.05}s` }}
                {...buildInteractiveProps(() => goToDetail('tenant', row.tenant_id))}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{row.tenant_name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{row.region} • {row.active_workforce} workforce • {row.status}</p>
                  </div>
                  <div className="text-right">
                    <p className={LIGHT_LABEL}>AI Risk</p>
                    <p className="mt-1 text-xl font-semibold text-rose-500">{row.ai_risk_score}</p>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-4">
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>Compliance</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{row.compliance_health}</p>
                  </div>
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>Attrition</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{row.attrition_risk}%</p>
                  </div>
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>Stability</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{row.workforce_stability}</p>
                  </div>
                  <div className={`${LIGHT_SUBPANEL} p-3`}>
                    <p className={LIGHT_LABEL}>Payroll</p>
                    <p className="mt-2 text-lg font-semibold text-foreground">{formatINR(row.payroll_volume)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid gap-6">
            <Card className="rounded-2xl border border-border bg-muted text-foreground shadow-sm">
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <CardTitle className="text-sm text-foreground">Risk Heatmap</CardTitle>
                <DetailActionButton onClick={() => goToDetail('section', 'multi-tenant-monitoring')} />
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-3">
                {(data.workforce_risk_heatmap || []).map((row) => (
                  <div
                    key={row.tenant_name}
                    className="cursor-pointer rounded-2xl border border-border bg-card p-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    style={{ background: `linear-gradient(135deg, rgba(244,63,94,${Math.min((row.risk || 0) / 180, 0.18)}), hsl(var(--card)))` }}
                    {...buildInteractiveProps(() => goToDetail('heatmap', slugify(row.tenant_name)))}
                  >
                    <p className="text-sm font-medium text-foreground">{row.tenant_name}</p>
                    <p className="mt-2 text-xs text-muted-foreground">Risk {row.risk} • Stability {row.stability}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="rounded-2xl border border-border bg-muted text-foreground shadow-sm">
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <CardTitle className="text-sm text-foreground">Tenant Benchmarking</CardTitle>
                <DetailActionButton onClick={() => goToDetail('section', 'risk-distribution')} />
              </CardHeader>
              <CardContent className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.tenant_health_matrix || []}>
                    <CartesianGrid {...gridProps} />
                    <XAxis dataKey="tenant_name" {...axisProps} />
                    <YAxis {...axisProps} />
                    <Tooltip contentStyle={tooltipContentStyle} cursor={tooltipCursor} />
                    <Bar dataKey="hiring_efficiency" radius={[6, 6, 0, 0]} fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      <Card className={LIGHT_PANEL}>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-foreground">Cross-Tenant Risk Distribution</CardTitle>
            <CardDescription className="text-muted-foreground">Relative AI risk position across all managed tenants.</CardDescription>
          </div>
          <DetailActionButton onClick={() => goToDetail('section', 'risk-distribution')} />
        </CardHeader>
        <CardContent className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
          {planMix.length === 0 ? (
            <div className="grid h-[320px] place-items-center">
              <EmptyState icon={Radar} title="No tenant risk data" description="Risk distribution will appear once tenants are monitored." compact />
            </div>
          ) : (
          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={planMix} dataKey="value" nameKey="name" innerRadius={60} outerRadius={105} paddingAngle={4}>
                  {planMix.map((entry, index) => (
                    <Cell key={entry.name} fill={chartColor(index)} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipContentStyle} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          )}
          <div className="space-y-3">
            {(data.tenant_health_matrix || []).map((row) => (
              <div
                key={row.tenant_id}
                className="cursor-pointer rounded-2xl border border-border bg-muted p-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                {...buildInteractiveProps(() => goToDetail('risk-distribution', row.tenant_id))}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{row.tenant_name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{row.region} • {row.department_focus}</p>
                  </div>
                  <div className="text-right">
                    <p className={LIGHT_LABEL}>Alerts</p>
                    <p className="mt-1 text-lg font-semibold text-foreground">{row.alert_count}</p>
                  </div>
                </div>
                <div className="mt-4 h-2 rounded-full bg-border">
                  <div className="h-2 rounded-full bg-gradient-to-r from-cyan-400 via-indigo-400 to-rose-400" style={{ width: `${Math.min(row.ai_risk_score, 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderAlertsSection = () => (
    <Card className={LIGHT_PANEL}>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-foreground">AI Alerts Center</CardTitle>
          <CardDescription className="text-muted-foreground">Payroll fraud, compliance drift, suspicious activity, and anomaly pressure.</CardDescription>
        </div>
        <DetailActionButton onClick={() => goToDetail('section', 'alerts-center')} />
      </CardHeader>
      <CardContent>
        <AIAnomalyTable rows={data.ai_anomaly_tracker || []} showTenant variant="light" onRowClick={(row) => goToDetail('alert', row.id)} />
      </CardContent>
    </Card>
  );

  const renderReportsSection = () => (
    <div className="grid gap-6">
      <Card className={LIGHT_PANEL}>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-foreground">Executive AI Reports</CardTitle>
            <CardDescription className="text-muted-foreground">AI-generated briefings for platform leadership.</CardDescription>
          </div>
          <DetailActionButton onClick={() => goToDetail('section', 'executive-reports')} />
        </CardHeader>
        <CardContent className="space-y-3">
          {(data.ai_executive_reports || []).map((report) => (
            <div
              key={report.title}
              className="cursor-pointer rounded-2xl border border-border bg-gradient-to-r from-primary/10 via-card to-card p-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              {...buildInteractiveProps(() => goToDetail('report', slugify(report.title)))}
            >
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                <Activity className="h-3.5 w-3.5 text-primary" />
                {report.tone}
              </div>
              <p className="mt-3 text-sm font-semibold text-foreground">{report.title}</p>
              <p className="mt-2 text-sm text-muted-foreground">{report.summary}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="cursor-pointer rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/40" {...buildInteractiveProps(() => goToDetail('predictive', 'attrition-forecasting'))}>
          <AIPredictiveTrendWidget
            title="Attrition Forecasting"
            description="Cross-tenant modeled attrition trend"
            data={data.predictive_analytics?.attrition_forecasting || []}
            color="#f43f5e"
            suffix="%"
            variant="light"
          />
        </div>
        <div className="cursor-pointer rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/40" {...buildInteractiveProps(() => goToDetail('predictive', 'workforce-forecasting'))}>
          <AIPredictiveTrendWidget
            title="Workforce Forecasting"
            description="Expected active workforce trajectory"
            data={data.predictive_analytics?.workforce_forecasting || []}
            color="#22d3ee"
            variant="light"
          />
        </div>
        <div className="cursor-pointer rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/40" {...buildInteractiveProps(() => goToDetail('predictive', 'payroll-forecasting'))}>
          <AIPredictiveTrendWidget
            title="Payroll Forecasting"
            description="Projected payroll movement"
            data={data.predictive_analytics?.payroll_forecasting || []}
            color="#10b981"
            variant="light"
            valueFormatter={formatINR}
          />
        </div>
      </div>
    </div>
  );

  const renderGovernanceSection = () => (
    <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
      <Card className={LIGHT_PANEL}>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-foreground">AI Governance Center</CardTitle>
            <CardDescription className="text-muted-foreground">Model health, prompt activity, decision confidence, and policy controls.</CardDescription>
          </div>
          <DetailActionButton onClick={() => goToDetail('section', 'governance-center')} />
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-3 sm:grid-cols-2">
            {(data.ai_governance_center?.model_health || []).map((row) => (
              <div
                key={row.name}
                className="cursor-pointer rounded-2xl border border-border bg-muted p-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                {...buildInteractiveProps(() => goToDetail('model-health', slugify(row.name)))}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground">{row.name}</p>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${row.status === 'healthy' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}`}>
                    {row.status}
                  </span>
                </div>
                <div className="mt-4 grid gap-2 text-sm text-muted-foreground">
                  <div className="flex justify-between"><span>Latency</span><span>{row.latency_ms} ms</span></div>
                  <div className="flex justify-between"><span>Confidence</span><span>{Math.round((row.confidence || 0) * 100)}%</span></div>
                  <div className="flex justify-between"><span>Throughput</span><span>{row.throughput}</span></div>
                </div>
              </div>
            ))}
          </div>
          <AIRecommendationPanel rows={data.ai_governance_center?.recommendation_approvals || []} variant="light" onRowClick={(row) => goToDetail('recommendation', row.id)} />
        </CardContent>
      </Card>

      <Card className={LIGHT_PANEL}>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-foreground">AI Activity Timeline</CardTitle>
            <CardDescription className="text-muted-foreground">Prompt operations, governance audits, and explainability events.</CardDescription>
          </div>
          <DetailActionButton onClick={() => goToDetail('section', 'activity-timeline')} />
        </CardHeader>
        <CardContent>
          <AIActivityTimeline rows={(data.ai_governance_center?.prompt_activity_logs || []).map((row) => ({
            id: row.id,
            title: row.prompt_text,
            subtitle: `${row.intent} • ${row.actor_role}`,
            meta: `${row.latency_ms} ms • ${row.confidence_score}% confidence`,
            created_at: row.created_at,
          }))} variant="light" onRowClick={(row) => goToDetail('activity', row.id)} />
        </CardContent>
      </Card>
    </div>
  );

  const renderServicesSection = () => (
    <Card className={LIGHT_PANEL}>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-foreground">AI Service Mesh</CardTitle>
          <CardDescription className="text-muted-foreground">Microservices architecture and operational status.</CardDescription>
        </div>
        <DetailActionButton onClick={() => goToDetail('section', 'service-mesh')} />
      </CardHeader>
      <CardContent className="space-y-3">
        {(data.ai_governance_center?.microservices || []).map((service) => (
          <div
            key={service.name}
            className="cursor-pointer rounded-2xl border border-border bg-card p-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            {...buildInteractiveProps(() => goToDetail('service', slugify(service.name)))}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="rounded-xl border border-primary/30 bg-primary/10 p-2">
                  <Cpu className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{service.name}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{service.runtime} • {service.mode}</p>
                </div>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${service.status === 'healthy' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}`}>
                {service.status}
              </span>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">{service.purpose}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );

  const renderActiveSection = () => {
    switch (currentSectionKey) {
      case 'copilot':
        return renderCopilotSection();
      case 'insights':
        return renderInsightsSection();
      case 'attendance-insight':
        return renderAttendanceInsightSection();
      case 'hiring-forecast':
        return renderHiringForecastSection();
      case 'payroll-forecast':
        return renderPayrollForecastSection();
      case 'team-performance':
        return renderTeamPerformanceSection();
      case 'monitoring':
        return renderMonitoringSection();
      case 'alerts':
        return renderAlertsSection();
      case 'reports':
        return renderReportsSection();
      case 'governance':
        return renderGovernanceSection();
      case 'services':
        return renderServicesSection();
      case 'overview':
      default:
        return renderOverviewSection();
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <StatSkeletonGrid count={4} />
        <StatSkeletonGrid count={4} />
      </div>
    );
  }

  return (
    <div className="space-y-6 text-foreground">
      <div className="space-y-3">
        <div>
          <p className={LIGHT_LABEL}>AI Dashboard</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-foreground">{currentSection.label}</h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{currentSection.description}</p>
        </div>

        {currentSectionKey === 'overview' ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {navigationCards.map((section) => {
              const Icon = section.icon;
              const isActive = section.key === currentSectionKey;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => onNavigate?.(section.id)}
                  className={`rounded-2xl border p-4 text-left transition ${
                    isActive
                      ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                      : 'border-border bg-card text-foreground hover:border-border hover:bg-muted'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className={`text-xs font-medium uppercase tracking-[0.18em] ${isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                        {section.label}
                      </p>
                      <p className={`mt-2 text-sm font-semibold ${isActive ? 'text-primary-foreground' : 'text-foreground'}`}>{section.metric}</p>
                    </div>
                    <Icon className={`h-5 w-5 ${isActive ? 'text-primary-foreground' : 'text-muted-foreground'}`} />
                  </div>
                  <p className={`mt-3 text-sm ${isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>{section.description}</p>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {renderActiveSection()}
    </div>
  );
};

export default SuperadminAICommandCenterPage;
